const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long a frontend session may use the backend API key it got at login.
 *
 * The backend's `app:cleanup:api_session` cron (daily at 18:05) deletes every `api_session` row
 * created more than 180 days ago, whatever its last use. The `_ast` cookie can't simply expire
 * first: Auth.js re-issues it with a fresh `maxAge` on every session read, so for an active user it
 * never expires, while the key inside it dies at 180 days (the backend then answers 401 and the
 * cookie is "dead"). One day of margin keeps us ahead of the cron.
 */
export const API_SESSION_MAX_AGE_MS = 179 * DAY_MS;

/**
 * `true` when the API key issued at `apiKeyIssuedAt` (epoch ms, stamped into the JWT at sign-in)
 * is about to be deleted by the backend, so the session should end now.
 *
 * Tokens minted before the stamp existed have no `apiKeyIssuedAt`: their age is unknown (the JWT's
 * own `iat` is re-signed on every session read), so they are left alone and keep today's behaviour
 * (the proxy heals them on the backend's 401). They pick up the stamp at their next login.
 */
export const isApiSessionExpired = (apiKeyIssuedAt: unknown, now: number = Date.now()): boolean => {
  if (typeof apiKeyIssuedAt !== 'number' || !Number.isFinite(apiKeyIssuedAt)) return false;

  return now - apiKeyIssuedAt >= API_SESSION_MAX_AGE_MS;
};
